import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Box, ButtonBase, IconButton, Typography, useTheme } from '@mui/material';
import clsx from 'clsx';
import { isHotkey } from 'is-hotkey';
import type { Descendant } from 'platejs';
import { useMount, usePrevious, useSessionStorage } from 'react-use';
import { AlineButton, AlineTooltip, Spacer } from 'src/components';
import { useAccessGuard } from 'src/components/auth';
import { ArrowUpIcon } from 'src/components/icons/ArrowUpIcon';
import { BookOpenIcon } from 'src/components/icons/BookOpenIcon';
import { CrossIcon } from 'src/components/icons/CrossIcon';
import { IntegrationBlocksIcon } from 'src/components/icons/IntegrationBlocksIcon';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { RevisionHistoryIcon } from 'src/components/icons/RevisionHistoryIcon';
import { SmallAiSparkleIcon } from 'src/components/icons/SmallAiSparkleIcon';
import { StopIcon } from 'src/components/icons/StopIcon';
import { UploadIcon } from 'src/components/icons/UploadIcon';
import { useStable } from 'src/editor/helpers/useStable';
import { Apollo } from 'src/gql/generated';
import { useNotificationDispatch } from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';
import { v4 as uuid4 } from 'uuid';
import { useSnapshot } from 'valtio';

import type { AlineMarkdownEditorRef } from './AlineMarkdownEditor';
import { AlineMarkdownEditor } from './AlineMarkdownEditor';
import type { PromptTab, TMentionElement } from './ChatEditor';
import {
  ChatEditorHistory,
  ChatEditorIntegrations,
  ChatEditorMarkdownPlugin,
  ChatEditorSavePrompt,
  deserializeMarkdownWithMentions,
  focusEditor,
  getEditorMarkdown,
  getIsEditorWithMarkdownApi,
  getIsMentionElement,
  isDocumentMentionElement,
  normalizeBlankMarkdown,
  PromptEditingLabel,
  SelectionMentionPastePlugin,
} from './ChatEditor';
import { renderAccessStatusContent } from './ChatEditor/ChatEditorMention/MentionAccess';
import { SavePromptDialog } from './SavePromptDialog';
import { useChatEditorDropzone } from '../chat-hooks/useChatEditorDropzone';
import { useStreamImprovePrompt } from '../chat-hooks/useImprovePrompt';
import { useInsertSelectionMentionV3 } from '../chat-hooks/useInsertSelectionMentionV3';
import type { InsertSelectionMentionV3Fn } from '../chat-hooks/useInsertSelectionMentionV3';
import {
  AccessRequestStatusContext,
  MentionEntityContext,
  useAccessRequestStatus,
} from '../chat-hooks/useMentionAccessContext';
import {
  extractDocumentChipIdsFromEditor,
  extractMentionEntityIdsFromEditor,
  useMentionEntitiesQuery,
} from '../chat-hooks/useMentionEntitiesQuery';
import { useRequestAccess } from '../chat-hooks/useRequestAccess';
import { $chatThreadState } from '../chat-state';

const CHAT_DRAFT_STORAGE_KEY = 'aline-chat-draft';
const chatBorderRadius = 16;
const useStyles = makeStyles<{ isDragging: boolean }>()((theme, { isDragging }) => ({
  addFilesButton: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 16,
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'center',
    minHeight: 32,
    minWidth: 32,
    padding: theme.spacing(0.5),
  },
  addFilesButtonIcon: {
    alignItems: 'center',
    backgroundColor: theme.appColors.primary,
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
    padding: theme.spacing(0.25),
  },
  addFilesButtonText: {
    /**
     * Hide text when container width is constrained (container query)
     * Only hide when panel is very narrow (e.g., resized document panel)
     */
    '@container (max-width: 350px)': {
      display: 'none',
    },
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  buttonContainer: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    gap: theme.spacing(0.5),
    margin: 0,
    padding: theme.spacing(1),
    width: '100%',
  },
  crossIcon: {
    height: 14,
    width: 14,
  },
  dropOverlay: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: chatBorderRadius,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    height: '100%',
    justifyContent: 'center',
    position: 'absolute',
    width: '100%',
    zIndex: 10,
  },
  dropOverlayIcon: {
    color: theme.palette.primary.main,
    fontSize: 32,
  },
  dropOverlayText: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  editorContainer: {
    '&:focus-within': {
      border: isDragging ? '1px solid transparent' : `1px solid ${theme.palette.primary.main}`,
    },
    border: isDragging ? '1px solid transparent' : `1px solid ${theme.appColors.rule}`,
    borderRadius: chatBorderRadius,
    boxSizing: 'border-box' as const,
    overflow: 'hidden',
    width: '100%',
  },
  iconButton: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 16,
    display: 'flex',
    justifyContent: 'center',
    minHeight: 32,
    minWidth: 32,
    padding: theme.spacing(0.5),
  },
  improvePromptButton: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    // Hide text when container is narrow
    '@container (max-width: 450px)': {
      borderRadius: 16,
      minHeight: 32,
      minWidth: 32,
      padding: theme.spacing(0, 0.5),
    },
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 16,
    color: theme.palette.primary.main,
    display: 'flex',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1.5),
  },
  improvePromptText: {
    '@container (max-width: 450px)': {
      display: 'none',
    },
    color: theme.palette.primary.main,
    fontSize: 14,
    fontWeight: 600,
  },

  returnIcon: {
    marginLeft: theme.spacing(0.5),
  },
  root: {
    containerType: 'inline-size',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
  },
  toolbarTopRow: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    height: 32,
    justifyContent: 'flex-start',
    padding: theme.spacing(0, 1.5),
  },
}));

export interface ChatPanelInputRef {
  getDocumentChipIds: () => string[];
  /** Returns true if the mentions were inserted, false if the editor wasn't ready. */
  insertMentions: (mentions: TMentionElement[]) => boolean;
  insertSelectionMentionV3: InsertSelectionMentionV3Fn;
  openAddContext: () => void;
  openHistory: () => void;
  openIntegrations: () => void;
  openSavePrompt: (tab?: PromptTab) => void;
  removeDocumentMentions: (ids: string[]) => void;
  /**
   * Replaces the whole draft with this markdown, mention links included, and
   * leaves the caret at the end. Returns false if the editor wasn't ready.
   */
  setPromptDraft: (markdown: string) => boolean;
}

export const ChatPanelInput = forwardRef<
  ChatPanelInputRef,
  {
    messages: SubmitButtonMessages;
    onNavigateToThread: (threadId: string) => void;
    onSubmit: (text: string, savedPromptId?: string) => void;
    threadId: UUID | null;
    className?: string;
    /** Fires after a thread is deleted from the history popover this input owns. */
    onThreadDeleted?: (threadId: UUID) => void;
    rootRef?: (element: HTMLDivElement | null) => void;
    visibleContext?: Apollo.AgentVisibleContextInput;
  }
>(function ChatPanelInputComponent(
  {
    className,
    messages,
    onNavigateToThread,
    onSubmit,
    onThreadDeleted,
    rootRef,
    threadId,
    visibleContext,
  },
  ref
) {
  const theme = useTheme();
  const dispatch = useNotificationDispatch();
  // Always defined for the history popover, and stable so it does not re-render on keystrokes.
  const handleThreadDeleted = useCallback(
    (deletedThreadId: UUID) => onThreadDeleted?.(deletedThreadId),
    [onThreadDeleted]
  );

  // Memoize plugins array - these never change but can't be module-level due to circular deps
  const chatEditorPlugins = useMemo(
    () => [SelectionMentionPastePlugin, ChatEditorMarkdownPlugin],
    []
  );

  const localRootRef = useRef<HTMLDivElement>(null);
  const setRootElement = useCallback(
    (element: HTMLDivElement | null) => {
      localRootRef.current = element;
      rootRef?.(element);
    },
    [rootRef]
  );

  const addContextButtonRef = useRef<HTMLButtonElement>(null);
  const historyButtonRef = useRef<HTMLButtonElement>(null);
  const integrationsButtonRef = useRef<HTMLButtonElement>(null);
  // Track current text for enabling/disabling submit button, persisted to session storage
  const chatState = useSnapshot($chatThreadState);
  const [currentText, setCurrentText] = useSessionStorage(CHAT_DRAFT_STORAGE_KEY, '');
  const [isRenamingTitle, setIsRenamingTitle] = useState(false);
  const markdownEditorRef = useRef<AlineMarkdownEditorRef>(null);
  // Track when editor is ready (needed to re-extract document IDs after restoring from session storage)
  const [editorReady, setEditorReady] = useState(false);
  const [updateSavedPrompt] = Apollo.useUpdateSavedPromptMutation();
  const prevEditingPromptId = usePrevious(chatState.editingPromptId);
  const [historyPopoverOpen, setHistoryPopoverOpen] = useState(false);
  const [savePromptDialogOpen, setSavePromptDialogOpen] = useState(false);
  const [savePromptPopoverOpen, setSavePromptPopoverOpen] = useState(false);
  const [integrationsPopoverOpen, setIntegrationsPopoverOpen] = useState(false);
  /**
   * Notify floating siblings (e.g. the artifacts bar) that a popover
   * is taking focus so they can collapse themselves.
   */
  const bumpInputPopover = useCallback(() => {
    $chatThreadState.inputPopoverOpenCount += 1;
  }, []);
  const [savePromptInitialTab, setSavePromptInitialTab] = useState<PromptTab>('my-saved');
  const [saveText, setSaveText] = useState<
    'Saved!' | 'Save changes?' | 'Save prompt?' | 'Save to my prompts?' | null
  >(null);
  const [improvePromptState, setImprovePromptState] = useState<'idle' | 'writing'>('idle');
  const [improveRequestId, setImproveRequestId] = useState<string | null>(null);
  const isEditing = Boolean(chatState.editingPromptId);
  const savedPromptOriginalText = chatState.editingPromptOriginalText ?? '';
  const hasUnsavedChanges =
    isEditing &&
    currentText.trim().length > 0 &&
    currentText.trim() !== savedPromptOriginalText.trim();

  // Show "Save prompt" when not editing and has content, "Save changes" when editing with changes and content
  const showSavePromptButton = !isEditing && currentText.trim().length > 0;
  const showSaveChangesButton =
    isEditing &&
    chatState.editingPromptIsEditable &&
    hasUnsavedChanges &&
    currentText.trim().length > 0;
  // Show "Save to my prompts" when using an Aline prompt (non-editable) with changes
  const showSaveAsNewButton =
    isEditing &&
    !chatState.editingPromptIsEditable &&
    hasUnsavedChanges &&
    currentText.trim().length > 0;
  const showImproveButton = Boolean(improvePromptState !== 'idle' || currentText.trim().length > 0);

  const computedSaveText = useMemo(() => {
    if (showSavePromptButton) return 'Save prompt?';
    if (showSaveChangesButton) return 'Save changes?';
    if (showSaveAsNewButton) return 'Save to my prompts?';
    return null;
  }, [showSavePromptButton, showSaveChangesButton, showSaveAsNewButton]);

  useEffect(() => {
    if (saveText === 'Saved!') return;
    setSaveText(computedSaveText);
  }, [saveText, computedSaveText]);

  const {
    dropzoneProps: { onPaste: handleFilePaste, ...dragDropProps },
    dropzoneRootRef: editorContainerRef,
    isDragging,
  } = useChatEditorDropzone({
    disabled: improvePromptState !== 'idle',
    editorRef: markdownEditorRef,
  });
  const { classes } = useStyles({ isDragging });
  const [improvePrompt] = Apollo.useImprovePromptMutation();
  const [updateSavedPromptTitle] = Apollo.useUpdateSavedPromptTitleMutation();
  /** Last serialized document-chip set, used to bump documentChipVersion only on change. */
  const prevDocumentChipKeyRef = useRef<string | null>(null);
  // Debounced markdown serialization to prevent excessive calls on every keystroke.
  const handleEditorChange = useDebouncedCallback(() => {
    const editor = markdownEditorRef.current?.getEditor();
    if (!editor) return;

    const newText = normalizeBlankMarkdown(getEditorMarkdown(editor).trim());
    setCurrentText(newText);

    /**
     * Notify out-of-tree subscribers (the repository "Add to Chat" toggle) when
     * the full-document chip set changes. Only bump on an actual change so we
     * don't re-render those subscribers on every keystroke.
     */
    const chipKey = extractDocumentChipIdsFromEditor(editor).sort().join(',');
    if (chipKey !== prevDocumentChipKeyRef.current) {
      prevDocumentChipKeyRef.current = chipKey;
      $chatThreadState.documentChipVersion += 1;
    }

    /**
     * Write directly to sessionStorage — useSessionStorage's write is async
     * (runs in a useEffect after the next render), so a page refresh before
     * that render would lose the update.
     */
    try {
      sessionStorage.setItem(CHAT_DRAFT_STORAGE_KEY, JSON.stringify(newText));
    } catch {
      // sessionStorage can throw in private mode
    }

    // If in editing/using mode and editor becomes empty, exit that mode
    if ($chatThreadState.editingPromptId && newText.length === 0) {
      $chatThreadState.editingPromptId = null;
      $chatThreadState.editingPromptIsEditable = true;
      $chatThreadState.editingPromptOriginalText = null;
      $chatThreadState.editingPromptTitle = null;
    }
  }, 100);

  /**
   * Run the draft-sync immediately, bypassing the typing debounce. Programmatic
   * mutations (repository "Add to Chat" / "Remove from Chat" inserting or
   * removing chips) otherwise leave `currentText`, `editorDocumentIds`, access
   * checks, submit enablement, and `documentChipVersion` stale for up to the
   * debounce window. Deliberate chip actions should reflect instantly.
   */
  const flushEditorChange = useCallback(() => {
    handleEditorChange();
    handleEditorChange.flush();
  }, [handleEditorChange]);

  /**
   * Flush the debounced callback on page refresh/close so the latest editor
   * content is written to sessionStorage before the page unloads.
   */
  useEffect(() => {
    const flushOnUnload = () => handleEditorChange.flush();
    window.addEventListener('beforeunload', flushOnUnload);
    return () => {
      window.removeEventListener('beforeunload', flushOnUnload);
    };
  }, [handleEditorChange]);

  /**
   * Extract document IDs from editor and query their access status.
   * Fires when currentText changes so we capture the latest document IDs.
   * Also re-extracts when editorReady changes (handles restored draft from session storage).
   */
  const editorMentionEntityIds = useStable(
    () => extractMentionEntityIdsFromEditor(markdownEditorRef.current?.getEditor() ?? null),
    [currentText, editorReady]
  );
  const {
    deletedDocumentIds,
    hasDeletedMentions,
    hasInaccessibleMentions,
    hasProcessingDocuments,
    inaccessibleDocumentIds,
    loading: checkingMentionAccess,
    mentionEntityContextValue,
  } = useMentionEntitiesQuery(editorMentionEntityIds);

  // Query access request status from server for inaccessible documents
  const {
    refetch: refetchAccessRequestStatus,
    statusMap: accessRequestStatusMap,
    updateStatus,
  } = useAccessRequestStatus(inaccessibleDocumentIds);
  const accessRequestStatusContextValue = useMemo(
    () => ({
      refetch: refetchAccessRequestStatus,
      statusMap: accessRequestStatusMap,
      updateStatus,
    }),
    [accessRequestStatusMap, refetchAccessRequestStatus, updateStatus]
  );

  // Check if all inaccessible documents have a pending access request
  const allAccessRequested = useMemo(() => {
    if (inaccessibleDocumentIds.length === 0) return false;
    return inaccessibleDocumentIds.every(
      id => accessRequestStatusMap.get(id) === Apollo.AccessRequestStatus.Pending
    );
  }, [inaccessibleDocumentIds, accessRequestStatusMap]);

  // Hook for requesting access to inaccessible documents
  const { requestAccess } = useRequestAccess();

  // Handler to request access for inaccessible documents
  const handleRequestAccess = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const result = await requestAccess(inaccessibleDocumentIds);
      // Update local state with returned data (avoids extra network request)
      if (result) updateStatus(result);
    },
    [inaccessibleDocumentIds, requestAccess, updateStatus]
  );

  // Handler to clear deleted document mentions from editor
  const handleClearDeleted = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const editor = markdownEditorRef.current?.getEditor();
      if (!editor || deletedDocumentIds.length === 0) return;

      const deletedSet = new Set(deletedDocumentIds);
      const mentionEntries = [
        ...editor.api.nodes({
          at: [],
          match: { type: 'mention' },
        }),
      ];

      // Remove from end to start to preserve paths
      for (let i = mentionEntries.length - 1; i >= 0; i--) {
        const [node, path] = mentionEntries[i];
        if (
          'documentId' in node &&
          typeof node.documentId === 'string' &&
          deletedSet.has(node.documentId)
        ) {
          editor.tf.removeNodes({ at: path });
        }
      }
    },
    [deletedDocumentIds]
  );

  const handleImprovePromptComplete = useCallback(() => {
    setImproveRequestId(null);
    setImprovePromptState('idle');
    if (markdownEditorRef.current) focusEditor(markdownEditorRef.current.getEditor(), 'end');
  }, []);

  useStreamImprovePrompt({
    editorRef: markdownEditorRef,
    improveRequestId,
    onComplete: handleImprovePromptComplete,
    onStreamStart: () => setImprovePromptState('writing'),
  });

  // Check if AI is currently streaming a response
  const hasStreamingMessage = messages.some(
    m =>
      m.role === Apollo.AgentMessageRole.Assistant &&
      m.status === Apollo.AgentMessageStatus.Streaming
  );

  // Check if AI is cancelling a response (stop was clicked but still buffering)
  const hasCancellingMessage = messages.some(
    m =>
      m.role === Apollo.AgentMessageRole.Assistant &&
      m.status === Apollo.AgentMessageStatus.Cancelling
  );

  // Reason to disable the submit button
  const submitDisabledReason = useMemo(() => {
    if (improvePromptState !== 'idle') return 'Improving prompt...';
    if (checkingMentionAccess) return 'Checking document access...';
    if (hasProcessingDocuments) return 'Documents are still being processed';
    if (hasInaccessibleMentions)
      return 'You do not have access to some documents mentioned in the chat';
    if (hasDeletedMentions) return 'Some documents mentioned in the chat have been deleted';
    return '';
  }, [
    improvePromptState,
    checkingMentionAccess,
    hasProcessingDocuments,
    hasInaccessibleMentions,
    hasDeletedMentions,
  ]);

  // Store threadId in editor options for selection/document mention helpers.
  useEffect(() => {
    const editor = markdownEditorRef.current?.getEditor();
    if (editor) editor.chatThreadId = threadId;
  }, [threadId]);

  useMount(() => {
    // Initialize cursor on mount.
    const editor = markdownEditorRef.current?.getEditor();
    if (!editor) return;
    // Mark editor as ready so document IDs are extracted from restored draft
    setEditorReady(true);
    // Position cursor at end if draft was restored, otherwise at start
    focusEditor(editor, currentText ? 'end' : 'start');
  });

  useEffect(() => {
    const editor = markdownEditorRef.current?.getEditor();
    // If we were editing and now we're not, clear the editor
    if (prevEditingPromptId && !chatState.editingPromptId && editor) {
      editor.tf.reset();
      setCurrentText('');
      setSaveText(null);
      flushEditorChange();
    }
  }, [chatState.editingPromptId, prevEditingPromptId, setCurrentText, flushEditorChange]);

  const handleSubmit = useCallback(async () => {
    const editor = markdownEditorRef.current?.getEditor();
    if (!editor) return;

    const markdown = normalizeBlankMarkdown(getEditorMarkdown(editor));
    if (!markdown) return;

    // Always send the message with the prompt ID if one was being used.
    onSubmit(markdown, chatState.editingPromptId ?? undefined);
    editor.tf.reset();
    setCurrentText('');
    /**
     * Clearing the editor removes its document chips, but reset() won't run the
     * debounced editor-change handler synchronously — flush so out-of-tree
     * subscribers (the repository "Add to Chat" toggle) see the chips disappear
     * immediately instead of staying on "Remove from Chat".
     */
    flushEditorChange();

    // Exit editing mode if active
    if (isEditing) {
      $chatThreadState.editingPromptId = null;
      $chatThreadState.editingPromptIsEditable = true;
      $chatThreadState.editingPromptOriginalText = null;
      $chatThreadState.editingPromptTitle = null;
    }
  }, [chatState.editingPromptId, onSubmit, isEditing, setCurrentText, flushEditorChange]);
  const guardedHandleSubmit = useAccessGuard({
    callback: handleSubmit,
    minRole: Apollo.OrgRole.Basic,
  });

  const handlePromptSaved = useCallback(
    (promptId: string, text: string, title: string) => {
      dispatch({
        payload: {
          key: uuid4(),
          message: 'Prompt saved successfully.',
          variant: 'success',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
      // Enter editing mode for the newly created prompt...
      $chatThreadState.editingPromptId = promptId;
      $chatThreadState.editingPromptIsEditable = true;
      $chatThreadState.editingPromptOriginalText = text;
      $chatThreadState.editingPromptTitle = title;
      if (markdownEditorRef.current) focusEditor(markdownEditorRef.current.getEditor());
    },
    [dispatch]
  );

  // Handle saving changes to an existing prompt
  const handleSaveChanges = useCallback(async () => {
    const editor = markdownEditorRef.current?.getEditor();
    if (!chatState.editingPromptId || !editor) return;

    const text = normalizeBlankMarkdown(getEditorMarkdown(editor).trim());
    if (!text) return;

    try {
      // Optimistically set to saved on purpose...
      setSaveText('Saved!');
      const { error } = await updateSavedPrompt({
        variables: {
          input: {
            id: chatState.editingPromptId,
            props: {
              text,
            },
          },
        },
      });
      if (error) {
        setSaveText(null);
        return;
      }

      $chatThreadState.editingPromptOriginalText = text;
    } finally {
      setTimeout(() => setSaveText(null), 2000);
      focusEditor(editor);
    }
  }, [chatState.editingPromptId, updateSavedPrompt]);

  const handleToolbarSaveClick = useCallback(() => {
    // "Save prompt?" or "Save to my prompts?" - open dialog to create new prompt
    if (!isEditing || showSaveAsNewButton) return setSavePromptDialogOpen(true);

    // "Save changes?" - update existing prompt
    handleSaveChanges();
  }, [isEditing, showSaveAsNewButton, handleSaveChanges]);

  // Handle renaming the prompt title inline (same pattern as ChatEditorSavePrompt)
  const handleRenamePrompt = useCallback(
    async (newTitle: string) => {
      const promptId = $chatThreadState.editingPromptId;
      if (!promptId) return;

      await updateSavedPromptTitle({
        optimisticResponse: {
          __typename: 'Mutation',
          updateSavedPrompt: {
            __typename: 'SavedPromptResponse',
            savedPrompt: {
              __typename: 'SavedPrompt',
              id: promptId,
              title: newTitle,
            },
          },
        },
        variables: {
          input: {
            id: promptId,
            props: {
              title: newTitle,
            },
          },
        },
      });

      $chatThreadState.editingPromptTitle = newTitle;
    },
    [updateSavedPromptTitle]
  );

  // Handle improving the prompt with AI
  const handleImprovePrompt = useCallback(async () => {
    const editor = markdownEditorRef.current?.getEditor();
    if (!editor) return;

    const markdown = normalizeBlankMarkdown(getEditorMarkdown(editor).trim());
    if (!markdown) return;

    const requestId = uuid4();
    setImprovePromptState('writing');
    setImproveRequestId(requestId);

    try {
      await improvePrompt({
        variables: { input: { originalText: markdown, requestId } },
      });
    } catch {
      setImprovePromptState('idle');
      setImproveRequestId(null);
      dispatch({
        payload: {
          key: uuid4(),
          message: 'Failed to improve prompt. Please try again.',
          variant: 'error',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
    }
  }, [improvePrompt, dispatch]);

  // Handle clearing the editor and exiting editing mode
  const handleClearPrompt = useCallback(() => {
    const editor = markdownEditorRef.current?.getEditor();
    // Exit editing mode first (before clearing editor to avoid race conditions)
    $chatThreadState.editingPromptId = null;
    $chatThreadState.editingPromptIsEditable = true;
    $chatThreadState.editingPromptOriginalText = null;
    $chatThreadState.editingPromptTitle = null;
    if (editor) {
      editor.tf.reset();
      // Use requestAnimationFrame to wait for DOM update after reset
      requestAnimationFrame(() => focusEditor(editor, 'start'));
    }

    setCurrentText('');
    setSaveText(null);
    flushEditorChange();
  }, [setCurrentText, flushEditorChange]);

  const openMentionPicker = useCallback(() => {
    markdownEditorRef.current?.openMentionPicker();
  }, []);

  // Handle keyboard events in the editor
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const editor = markdownEditorRef.current?.getEditor();
      if (!editor) return;

      // Handle Escape key to clear chat editor content.
      if (isHotkey('escape', e.nativeEvent)) {
        e.preventDefault();
        // Exit edit mode first.
        $chatThreadState.editingPromptId = null;
        $chatThreadState.editingPromptIsEditable = true;
        $chatThreadState.editingPromptOriginalText = null;
        $chatThreadState.editingPromptTitle = null;
        // Clear editor content - focus after next frame to ensure DOM is updated.
        editor.tf.setValue([{ children: [{ text: '' }], type: 'p' }]);
        setSaveText(null);
        setCurrentText('');
        flushEditorChange();
        // Use requestAnimationFrame to wait for DOM update after setValue.
        requestAnimationFrame(() => focusEditor(editor, 'start'));
        return;
      }

      // Handle Space key to move cursor outside link
      if (e.key === ' ' && editor.selection) {
        try {
          const linkEntry = editor.api.node({
            match: { type: 'a' },
          });
          if (linkEntry) {
            // Check if cursor is at the end of the link
            if (editor.selection) {
              // Move cursor after the link and insert space
              e.preventDefault();
              editor.tf.move({ distance: 1, unit: 'offset' });
              editor.tf.insertText(' ');
              return;
            }
          }
        } catch {
          // If any error, just let space work normally.
        }
      }

      if (isHotkey('mod+enter', e.nativeEvent)) {
        e.preventDefault();
        /**
         * Don't submit if already streaming, cancelling, documents are still processing,
         * or there are inaccessible/deleted mentions.
         */
        if (hasStreamingMessage || hasCancellingMessage) return;

        if (hasProcessingDocuments) {
          dispatch({
            payload: {
              key: uuid4(),
              message: 'Documents are still being processed.',
              variant: 'warning',
            },
            type: NotificationsActionType.PUSH_NOTIFICATION,
          });
          return;
        }

        if (hasInaccessibleMentions || hasDeletedMentions) {
          dispatch({
            payload: {
              children: (
                <>
                  {renderAccessStatusContent({
                    accessRequested: allAccessRequested,
                    deletedCount: deletedDocumentIds.length,
                    inaccessibleCount: inaccessibleDocumentIds.length,
                    onClearDeleted: handleClearDeleted,
                    onRequestAccess: handleRequestAccess,
                  })}
                </>
              ),
              key: uuid4(),
              variant: 'warning',
            },
            type: NotificationsActionType.PUSH_NOTIFICATION,
          });
          return;
        }

        // All checks pass.  Submit.
        guardedHandleSubmit();
      }
    },
    [
      allAccessRequested,
      deletedDocumentIds.length,
      dispatch,
      handleClearDeleted,
      handleRequestAccess,
      guardedHandleSubmit,
      hasCancellingMessage,
      hasDeletedMentions,
      hasInaccessibleMentions,
      hasProcessingDocuments,
      hasStreamingMessage,
      inaccessibleDocumentIds.length,
      setCurrentText,
      flushEditorChange,
    ]
  );

  const insertSelectionMentionV3 = useInsertSelectionMentionV3(markdownEditorRef);

  useImperativeHandle(ref, () => ({
    getDocumentChipIds: () => {
      return extractDocumentChipIdsFromEditor(markdownEditorRef.current?.getEditor() ?? null);
    },
    insertMentions: (mentions: TMentionElement[]) => {
      const editor = markdownEditorRef.current?.getEditor();
      if (!editor || mentions.length === 0) return false;
      // When called from outside the editor (e.g. the repository "Add to Chat"
      // button or the deliverables bar), editor.selection is null because Slate
      // deselects on blur. Explicitly move to the end so insertNodes has a valid
      // anchor point.
      if (!editor.selection) focusEditor(editor, 'end');
      editor.tf.withoutNormalizing(() => {
        editor.tf.removeNodes({ at: [], match: n => n.type === 'mention_input' });
        const nodes: Descendant[] = mentions.flatMap(mention => [mention, { text: ' ' }]);
        editor.tf.insertNodes(nodes, { select: true });
      });
      focusEditor(editor);
      flushEditorChange();
      return true;
    },
    insertSelectionMentionV3,
    openAddContext: () => {
      openMentionPicker();
    },
    openHistory: () => {
      bumpInputPopover();
      setHistoryPopoverOpen(true);
    },
    openIntegrations: () => {
      bumpInputPopover();
      setIntegrationsPopoverOpen(true);
    },
    openSavePrompt: (tab = 'my-saved') => {
      bumpInputPopover();
      setSavePromptInitialTab(tab);
      setSavePromptPopoverOpen(true);
    },
    removeDocumentMentions: (ids: string[]) => {
      const editor = markdownEditorRef.current?.getEditor();
      if (!editor || ids.length === 0) return;
      const idSet = new Set(ids);
      const mentionEntries = [
        ...editor.api.nodes({
          at: [],
          match: { type: 'mention' },
        }),
      ];
      for (let i = mentionEntries.length - 1; i >= 0; i--) {
        const [node, path] = mentionEntries[i];
        if (!getIsMentionElement(node)) continue;
        /**
         * Only remove full-document chips. Selection / selection-v2 mentions
         * share the same documentId but represent pasted excerpts, so removing
         * the document from chat must not silently strip them.
         */
        if (!isDocumentMentionElement(node)) continue;
        if (idSet.has(node.documentId)) editor.tf.removeNodes({ at: path });
      }
      flushEditorChange();
    },
    setPromptDraft: (markdown: string) => {
      const editor = markdownEditorRef.current?.getEditor();
      if (!editor || !getIsEditorWithMarkdownApi(editor)) return false;
      /**
       * A prefilled prompt is a fresh request, not an edit of a saved one, so
       * any prompt being edited is let go before the draft is replaced.
       */
      $chatThreadState.editingPromptId = null;
      $chatThreadState.editingPromptIsEditable = true;
      $chatThreadState.editingPromptOriginalText = null;
      $chatThreadState.editingPromptTitle = null;
      editor.tf.setValue(deserializeMarkdownWithMentions(editor, markdown));
      setCurrentText(getEditorMarkdown(editor));
      flushEditorChange();
      focusEditor(editor, 'end');
      return true;
    },
  }));

  return (
    <>
      <div
        ref={setRootElement}
        className={clsx(classes.root, className)}
        onClick={() => {
          if (markdownEditorRef.current) focusEditor(markdownEditorRef.current.getEditor());
        }}
      >
        {/* Editor container with border - contains everything; ref comes from dragDropProps */}
        <div
          className={classes.editorContainer}
          style={{ position: 'relative' }}
          {...dragDropProps}
        >
          {/* Drop overlay shown when dragging files over editor */}
          {isDragging && (
            <div className={classes.dropOverlay}>
              <UploadIcon className={classes.dropOverlayIcon} />
              <Typography className={classes.dropOverlayText} variant="body1">
                Drop files to upload
              </Typography>
            </div>
          )}

          {/* Top row - prompt editing label or "New Prompt" + save actions */}
          <Box className={classes.toolbarTopRow}>
            {chatState.editingPromptId && chatState.editingPromptTitle ? (
              <PromptEditingLabel
                hasUnsavedChanges={hasUnsavedChanges}
                isEditable={chatState.editingPromptIsEditable}
                title={chatState.editingPromptTitle}
                onClear={handleClearPrompt}
                onRename={handleRenamePrompt}
                onRenamingChange={setIsRenamingTitle}
              />
            ) : (
              <Typography color="textSecondary" sx={{ ml: 0.75 }} variant="caption">
                New Prompt
              </Typography>
            )}
            <Spacer />
            {saveText && !isRenamingTitle && (
              <Typography
                noWrap
                component="span"
                sx={{
                  '&:hover': {
                    textDecoration:
                      showSavePromptButton || showSaveChangesButton || showSaveAsNewButton
                        ? 'underline'
                        : 'none',
                  },
                  color: theme.palette.primary.main,
                  cursor:
                    showSavePromptButton || showSaveChangesButton || showSaveAsNewButton
                      ? 'pointer'
                      : 'default',
                  fontSize: '0.8125rem',
                  fontWeight: 'bold',
                  opacity: improvePromptState !== 'idle' ? 0.5 : 1,
                  pointerEvents: improvePromptState !== 'idle' ? 'none' : 'auto',
                }}
                onClick={handleToolbarSaveClick}
              >
                {saveText}
              </Typography>
            )}
          </Box>

          {/* AlineMarkdownEditor - toolbar and textarea (no border, parent provides it) */}
          <MentionEntityContext.Provider value={mentionEntityContextValue}>
            <AccessRequestStatusContext.Provider value={accessRequestStatusContextValue}>
              <AlineMarkdownEditor
                ref={markdownEditorRef}
                noBorder
                autoFocus={!savePromptDialogOpen}
                disabled={improvePromptState !== 'idle'}
                extraPlugins={chatEditorPlugins}
                maxHeight={300}
                mentions={{
                  allowFileUpload: true,
                  allowIntakeFormMention: Boolean(visibleContext?.workflowId),
                  quickTypes: [Apollo.MentionEntitySearchType.Document],
                }}
                minHeight={80}
                placeholder="Ask Aline AI anything..."
                readOnly={improvePromptState !== 'idle'}
                value={currentText}
                onChange={handleEditorChange}
                onKeyDown={handleKeyDown}
                onPaste={handleFilePaste}
              />
            </AccessRequestStatusContext.Provider>
          </MentionEntityContext.Provider>

          {/* Bottom row - action buttons */}
          <div className={classes.buttonContainer}>
            {/* Add context button - opens floating mention menu */}
            <AlineTooltip arrow placement="top" title="Add context to chat">
              <div>
                <IconButton
                  ref={addContextButtonRef}
                  className={classes.addFilesButton}
                  disabled={improvePromptState !== 'idle'}
                  onClick={openMentionPicker}
                >
                  <CrossIcon
                    style={{
                      height: 18,
                      width: 18,
                    }}
                  />
                </IconButton>
              </div>
            </AlineTooltip>

            {/* Integration button */}
            <AlineTooltip arrow placement="top" title="View integrations">
              <div>
                <IconButton
                  ref={integrationsButtonRef}
                  className={classes.iconButton}
                  disabled={improvePromptState !== 'idle'}
                  size="small"
                  style={
                    integrationsPopoverOpen
                      ? {
                          backgroundColor: theme.palette.action.hover,
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                        }
                      : undefined
                  }
                  onClick={() => {
                    bumpInputPopover();
                    setIntegrationsPopoverOpen(true);
                  }}
                >
                  <IntegrationBlocksIcon style={{ height: 16, width: 16 }} />
                </IconButton>
              </div>
            </AlineTooltip>

            {/* Bookmarked prompts button */}
            <AlineTooltip arrow placement="top" title="View saved prompts">
              <div>
                <IconButton
                  className={classes.iconButton}
                  disabled={improvePromptState !== 'idle'}
                  size="small"
                  style={
                    savePromptPopoverOpen || isEditing
                      ? {
                          backgroundColor: theme.palette.action.hover,
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                        }
                      : undefined
                  }
                  onClick={() => {
                    bumpInputPopover();
                    setSavePromptPopoverOpen(true);
                  }}
                >
                  <BookOpenIcon style={{ height: 16, width: 16 }} />
                </IconButton>
              </div>
            </AlineTooltip>

            {/* History button */}
            <AlineTooltip arrow placement="top" title="View chat history">
              <div>
                <IconButton
                  ref={historyButtonRef}
                  className={classes.iconButton}
                  disabled={improvePromptState !== 'idle'}
                  size="small"
                  style={
                    historyPopoverOpen
                      ? {
                          backgroundColor: theme.palette.action.hover,
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                        }
                      : undefined
                  }
                  onClick={() => {
                    bumpInputPopover();
                    setHistoryPopoverOpen(true);
                  }}
                >
                  <RevisionHistoryIcon style={{ height: 20, width: 20 }} />
                </IconButton>
              </div>
            </AlineTooltip>

            {/* Improve prompt with AI button */}
            {showImproveButton && (
              <ButtonBase
                className={classes.improvePromptButton}
                disabled={improvePromptState !== 'idle'}
                onClick={handleImprovePrompt}
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    height: 14,
                    justifyContent: 'center',
                    width: 14,
                  }}
                >
                  {improvePromptState !== 'idle' ? (
                    <PulseIcon variant={PulseIconVariant.LoadingPrimary} />
                  ) : (
                    <SmallAiSparkleIcon fill={theme.palette.primary.main} />
                  )}
                </Box>
                <Typography className={classes.improvePromptText} component="span">
                  {improvePromptState === 'writing' ? 'AI is writing...' : 'Improve'}
                </Typography>
              </ButtonBase>
            )}
            <Spacer />
            <ChatSubmitButton
              accessRequested={allAccessRequested}
              deletedCount={deletedDocumentIds.length}
              disabledReason={submitDisabledReason}
              dispatch={dispatch}
              inaccessibleCount={inaccessibleDocumentIds.length}
              messages={messages}
              prompt={currentText}
              threadId={threadId}
              onClearDeleted={handleClearDeleted}
              onRequestAccess={handleRequestAccess}
              onSubmit={guardedHandleSubmit}
            />
          </div>
        </div>
      </div>
      <ChatEditorHistory
        anchorEl={editorContainerRef.current}
        open={historyPopoverOpen}
        onClose={() => {
          setHistoryPopoverOpen(false);
          if (markdownEditorRef.current) focusEditor(markdownEditorRef.current.getEditor());
        }}
        onNavigateToThread={onNavigateToThread}
        onThreadDeleted={handleThreadDeleted}
      />
      <ChatEditorSavePrompt
        anchorEl={editorContainerRef.current}
        initialTab={savePromptInitialTab}
        open={savePromptPopoverOpen}
        onClose={() => {
          setSavePromptPopoverOpen(false);
          setSavePromptInitialTab('my-saved');
          if (markdownEditorRef.current) focusEditor(markdownEditorRef.current.getEditor());
        }}
        onSelectPrompt={(text: string, promptId: string, title: string, isEditable: boolean) => {
          const editor = markdownEditorRef.current?.getEditor();
          if (!editor || !getIsEditorWithMarkdownApi(editor)) return;

          editor.tf.setValue(deserializeMarkdownWithMentions(editor, text));
          /**
           * Enter editing mode for the selected prompt.
           * Use re-serialized markdown as original text to avoid false "Save changes?" due to
           * markdown round-trip differences (e.g., table formatting, whitespace normalization)
           */
          const serializedText = getEditorMarkdown(editor);
          $chatThreadState.editingPromptId = promptId;
          $chatThreadState.editingPromptIsEditable = isEditable;
          $chatThreadState.editingPromptOriginalText = serializedText;
          $chatThreadState.editingPromptTitle = title;
          setCurrentText(serializedText);
          setSavePromptPopoverOpen(false);
          focusEditor(editor, 'start');
        }}
      />
      <ChatEditorIntegrations
        anchorEl={editorContainerRef.current}
        open={integrationsPopoverOpen}
        onClose={() => {
          setIntegrationsPopoverOpen(false);
          if (markdownEditorRef.current) focusEditor(markdownEditorRef.current.getEditor());
        }}
      />

      {/* Save Prompt Title Dialog */}
      <SavePromptDialog
        open={savePromptDialogOpen}
        promptText={markdownEditorRef.current?.getMarkdown() ?? ''}
        templatePromptId={showSaveAsNewButton ? chatState.editingPromptId : null}
        onClose={() => {
          setSavePromptDialogOpen(false);
          if (markdownEditorRef.current) focusEditor(markdownEditorRef.current.getEditor());
        }}
        onSave={handlePromptSaved}
      />
    </>
  );
});

ChatPanelInput.displayName = 'ChatPanelInput';

type SubmitButtonMessages = Pick<Apollo.AgentMessage, 'id' | 'role' | 'status'>[];

function ChatSubmitButton({
  accessRequested,
  deletedCount,
  disabledReason = '',
  inaccessibleCount,
  messages,
  onClearDeleted,
  onRequestAccess,
  onSubmit,
  prompt,
  threadId,
}: {
  accessRequested: boolean;
  deletedCount: number;
  dispatch: ReturnType<typeof useNotificationDispatch>;
  inaccessibleCount: number;
  messages: SubmitButtonMessages;
  onClearDeleted: (e: React.MouseEvent) => void;
  onRequestAccess: (e: React.MouseEvent) => void;
  onSubmit: () => void;
  prompt: string;
  threadId: UUID | null;
  disabledReason?: string;
}) {
  const theme = useTheme();
  const [stopChatMessageStream] = Apollo.useStopChatMessageStreamMutation();

  // Track when user has clicked stop - stays true until streaming actually ends
  const [isStopping, setIsStopping] = useState(false);

  const isStreaming =
    !!threadId &&
    messages.some(
      m =>
        m.role === Apollo.AgentMessageRole.Assistant &&
        m.status === Apollo.AgentMessageStatus.Streaming
    );

  // Check if all assistant messages have reached a terminal state (Complete or Cancelled)
  const allMessagesFinished =
    !threadId ||
    messages
      .filter(m => m.role === Apollo.AgentMessageRole.Assistant)
      .every(
        m =>
          m.status === Apollo.AgentMessageStatus.Complete ||
          m.status === Apollo.AgentMessageStatus.Cancelled
      );

  // Reset isStopping only when messages have actually finished (terminal state)
  useEffect(() => {
    if (allMessagesFinished && isStopping) setIsStopping(false);
  }, [allMessagesFinished, isStopping]);

  const trimmedPrompt = prompt.trim();
  const isSubmitDisabled = Boolean(disabledReason);
  const buttonDisabled = !trimmedPrompt || isSubmitDisabled;

  if (isStreaming || isStopping)
    return (
      <AlineButton
        color="primary"
        disabled={isStopping}
        style={{
          padding: theme.spacing(0, 1.5),
        }}
        variant="outlined"
        onClick={async (e: React.MouseEvent) => {
          e.preventDefault();
          if (isStreaming && !isStopping) {
            setIsStopping(true);
            await stopChatMessageStream({ variables: { threadId } });
          }
        }}
      >
        <Typography
          noWrap
          color="inherit"
          component="span"
          sx={{
            fontWeight: 'bold',
          }}
        >
          {isStopping ? 'Stopping' : 'Stop'}
        </Typography>
        <Box style={{ alignItems: 'center', display: 'flex', marginLeft: theme.spacing(0.5) }}>
          <StopIcon height={16} stroke="currentColor" width={16} />
        </Box>
      </AlineButton>
    );

  const accessContent = renderAccessStatusContent({
    accessRequested,
    deletedCount,
    inaccessibleCount,
    onClearDeleted,
    onRequestAccess,
  });

  const getTooltipContent = () => {
    if (!disabledReason) return 'Send message (⌘+Enter)';
    return accessContent ?? disabledReason;
  };

  return (
    <AlineTooltip arrow placement="top" title={getTooltipContent()}>
      <div>
        <AlineButton
          color="primary"
          disabled={buttonDisabled}
          style={{
            padding: theme.spacing(0, 1.5),
          }}
          variant="contained"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <Typography
            noWrap
            color="inherit"
            component="span"
            sx={{
              fontWeight: 'bold',
            }}
          >
            Send
          </Typography>
          <Box style={{ alignItems: 'center', display: 'flex', marginLeft: theme.spacing(0.5) }}>
            <ArrowUpIcon height={16} width={16} />
          </Box>
        </AlineButton>
      </div>
    </AlineTooltip>
  );
}
